import React from 'react';
import BayWindowSchematic from './BayWindowSchematic';
import { GrilleOverlay, normalizePanes } from './WindowGrilleOverlay';
import {
  casementPanelCount,
  DEFAULT_GRILLE_PATTERN,
  hungUnitCount,
  slidingPanelCount,
} from '../../../constants/windowDefaults';

const VB_W = 100;
const VB_H = 100;

/**
 * Procedural window schematic for elevation views. Dispatches on
 * `category.window.style`:
 *   double-hung   — horizontal meeting rail across the middle
 *   single-hung   — same as double-hung; only the lower sash is
 *                   operable (no visual difference at this scale)
 *   casement      — single sash, or a centre mullion once the window is wide
 *                   enough for a pair. The count is DERIVED from the width
 *                   (`casementPanelCount`), exactly as the 3D renderer does
 *                   it; `category.window.panels` is a manual override
 *   picture       — single fixed sash, no meeting rail
 *   sliding       — `slidingPanelCount` leaves with a meeting stile between
 *                   each pair; `category.window.panels` overrides it
 *   bay           — handed to `BayWindowSchematic`: three units drawn
 *                   in metres from the 3D bay's own profile, plus a
 *                   projection profile for the side views
 *
 * GrilleOverlay (`WindowGrilleOverlay.jsx`) draws a `panes.x × panes.y`
 * divided-light grid on top, using `grillePattern` ('none' | 'colonial'
 * | 'prairie') and honouring `upperGrilleOnly` for hung windows.
 *
 * Front view: glazing geometry. Side view: thin jamb-width slab +
 * small protruding sill at the bottom front; symmetric, so L and R
 * render identically.
 *
 * Stroke / selection / capture wiring is owned by the caller.
 */
function WindowElevationSchematic(props) {
  const { obj, view = 'front' } = props;
  const winCat =
    obj && typeof obj.category?.window === 'object' ? obj.category.window : {};
  if (winCat.style === 'bay') {
    return <BayWindowSchematic {...props} />;
  }
  if (view === 'left' || view === 'right') {
    return (
      <WindowSideView
        view={view}
        depthMeters={props.depthMeters}
        heightMeters={props.heightMeters}
        strokeColor={props.strokeColor}
        strokeWidth={props.strokeWidth}
      />
    );
  }
  return (
    <WindowFrontView
      winCat={winCat}
      // The casement mullion is a function of the real WIDTH, and the front
      // view is drawn in a unit viewBox — so the width has to be handed down.
      widthMeters={props.widthMeters ?? obj?.dimensions?.x}
      strokeColor={props.strokeColor}
      strokeWidth={props.strokeWidth}
    />
  );
}

WindowElevationSchematic.supportsSideViews = true;

function WindowFrontView({ winCat, widthMeters, strokeColor, strokeWidth }) {
  const style = winCat.style || 'double-hung';
  const panes = normalizePanes(winCat.panes);
  const grillePattern = winCat.grillePattern || DEFAULT_GRILLE_PATTERN;
  const upperGrilleOnly = !!winCat.upperGrilleOnly;
  const grilleStroke = Math.max(strokeWidth * 0.5, 0.3);

  // The sash rectangle is the frame inset; grilles only draw inside
  // it so they never collide with the outer frame.
  const sash = { x: 4, y: 4, w: 92, h: 92 };

  // How this window divides. Every sash is its own light and gets its own
  // grille — a casement pair, a slider's leaves, and a hung window's upper and
  // lower. Drawing one grid across the whole opening instead put a muntin at
  // dead centre, exactly where the stile or meeting rail already is: the two
  // collapsed onto the same coordinate and a grilled window read as one sash.
  const grid = glazingGrid(style, sash, {
    panelCount: winCat.panels || casementPanelCount(widthMeters),
    unitCount: winCat.panels || hungUnitCount(widthMeters),
    leafCount: winCat.panels || slidingPanelCount(widthMeters),
  });

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        overflow: 'visible',
      }}
    >
      <rect
        x={0}
        y={0}
        width={VB_W}
        height={VB_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <rect
        x={sash.x}
        y={sash.y}
        width={sash.w}
        height={sash.h}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <StyleDividers
        grid={grid}
        sash={sash}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {grid.regions
        // `upperGrilleOnly` means the LOWER SASH has no grille — the same rule
        // 3D applies (`skipGrille` on `sash.name === 'lower'`). It used to
        // clamp one grid to the top half of the opening, which is a different
        // drawing: half-height lights in a full-height sash.
        .filter((region) => !(upperGrilleOnly && region.row > 0))
        .map((region) => (
          <GrilleOverlay
            key={`grille-${region.col}-${region.row}`}
            sash={region}
            panes={panes}
            grillePattern={grillePattern}
            strokeColor={strokeColor}
            strokeWidth={grilleStroke}
          />
        ))}
    </svg>
  );
}

/**
 * Side elevation of a flat-facade window: a thin jamb-width slab + a
 * small projecting sill at the bottom of the FRONT edge. Symmetric —
 * the RIGHT view mirrors the LEFT but produces an indistinguishable
 * silhouette since the sill projection is the only asymmetric
 * feature in the depth direction. We mirror via transform anyway so
 * the convention (front-edge faces outward in L) is preserved if a
 * future spec adds an asymmetric feature.
 */
function WindowSideView({
  view,
  depthMeters,
  heightMeters,
  strokeColor,
  strokeWidth,
}) {
  const w = depthMeters;
  const h = heightMeters;
  const sillProjection = Math.min(w * 0.4, 0.04);
  const sillHeight = Math.min(h * 0.04, 0.05);
  const sideChildren = (
    <>
      <line
        x1={w}
        y1={h - sillHeight}
        x2={w + sillProjection}
        y2={h - sillHeight}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w + sillProjection}
        y1={h - sillHeight}
        x2={w + sillProjection}
        y2={h}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={w}
        y1={h}
        x2={w + sillProjection}
        y2={h}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </>
  );
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {view === 'right' ? (
        <g transform={`translate(${w}, 0) scale(-1, 1)`}>{sideChildren}</g>
      ) : (
        sideChildren
      )}
    </svg>
  );
}

/**
 * How a window of this style divides into GLAZED AREAS, as a cols × rows grid.
 *
 * This is the ONE place that answers it, so the structural dividers and the
 * grilles cannot disagree: `StyleDividers` draws a line at every internal
 * boundary of the grid, and one grille is drawn INSIDE each cell. Both are
 * therefore derived from the same numbers.
 *
 * The counts mirror `WindowWithTrim` exactly, because `panes` is a PER-SASH
 * grid in the 3D renderer — it hands the full count to each casement panel and
 * to each hung sash — and an elevation that spread one grid over the whole
 * opening drew a different window from the one in 3D.
 *
 *   casement — `panelCount` sashes side by side
 *   sliding  — `leafCount` leaves side by side
 *   hung     — `unitCount` mulled units across, each split upper / lower
 *   picture  — one light
 */
function glazingGrid(style, sash, { panelCount, unitCount, leafCount }) {
  let cols = 1;
  let rows = 1;
  if (style === 'casement') cols = Math.max(1, Math.floor(panelCount) || 1);
  else if (style === 'sliding') cols = Math.max(1, Math.floor(leafCount) || 1);
  else if (style === 'double-hung' || style === 'single-hung') {
    cols = Math.max(1, Math.floor(unitCount) || 1);
    rows = 2; // the meeting rail
  }

  const regions = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      regions.push({
        x: sash.x + (sash.w * c) / cols,
        y: sash.y + (sash.h * r) / rows,
        w: sash.w / cols,
        h: sash.h / rows,
        col: c,
        row: r,
      });
    }
  }
  return { cols, rows, regions };
}

/**
 * The structure: a line at each internal boundary of the glazing grid — a
 * casement stile, a slider's meeting rail, a hung window's mullions and its
 * meeting rail. A single-light window has no internal boundary and so draws
 * nothing, which is what a picture window is.
 */
function StyleDividers({ grid, sash, strokeColor, strokeWidth }) {
  const line = (key, x1, y1, x2, y2) => (
    <line
      key={key}
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );

  const lines = [];
  for (let c = 1; c < grid.cols; c += 1) {
    const x = sash.x + (sash.w * c) / grid.cols;
    lines.push(line(`stile-${c}`, x, sash.y, x, sash.y + sash.h));
  }
  for (let r = 1; r < grid.rows; r += 1) {
    const y = sash.y + (sash.h * r) / grid.rows;
    lines.push(line(`rail-${r}`, sash.x, y, sash.x + sash.w, y));
  }
  return lines.length ? <>{lines}</> : null;
}

export default WindowElevationSchematic;
